import { type ChangeEvent, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { MessageQuestion, Copy, Sparkles } from '@/components/icons';
import { PageSection } from '@/components/system';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { calculatePercentile, calculateTScore, calculateZScore } from '@/utils/statistics';
import { formatMetricValue, parseMetricInput } from '../utils';

type ZScoreField = 'raw' | 'mean' | 'standardDeviation';

type ZScoreInputs = Record<ZScoreField, string>;

type ZScoreResult = {
    z: number;
    t: number;
    percentile: number;
};

type ZScoreCalculation = {
    data: ZScoreResult | null;
    error?: string;
};

type CopyableMetricCardProps = {
    title: string;
    value: string;
    meta: string;
    copyText: string | null;
    onCopy: (text: string) => void;
};

const DEFAULT_INPUTS: ZScoreInputs = {
    raw: '85',
    mean: '70',
    standardDeviation: '10',
};

const FIELD_CONFIG: Array<{
    id: ZScoreField;
    label: string;
    helper: string;
    placeholder: string;
}> = [
    {
        id: 'raw',
        label: 'Resultado bruto',
        helper: 'Pontuação observada da pessoa avaliada.',
        placeholder: 'Ex.: 85',
    },
    {
        id: 'mean',
        label: 'Média',
        helper: 'Média do grupo normativo ou da amostra usada como referência.',
        placeholder: 'Ex.: 70',
    },
    {
        id: 'standardDeviation',
        label: 'Desvio padrão',
        helper: 'Medida de dispersão da referência. Precisa ser maior que zero.',
        placeholder: 'Ex.: 10',
    },
];

const FIELD_LABELS: Record<ZScoreField, string> = {
    raw: 'resultado bruto',
    mean: 'média',
    standardDeviation: 'desvio padrão',
};

function resolveZScore(inputs: ZScoreInputs): ZScoreCalculation {
    const emptyFields = FIELD_CONFIG.filter((field) => inputs[field.id].trim() === '');

    if (emptyFields.length === FIELD_CONFIG.length) {
        return { data: null };
    }

    if (emptyFields.length > 0) {
        const missingLabels = emptyFields.map((field) => FIELD_LABELS[field.id]).join(', ');

        return {
            data: null,
            error: `Preencha ${missingLabels} para calcular o escore Z.`,
        };
    }

    const raw = parseMetricInput(inputs.raw);
    const mean = parseMetricInput(inputs.mean);
    const standardDeviation = parseMetricInput(inputs.standardDeviation);

    if (raw === null || mean === null || standardDeviation === null) {
        return {
            data: null,
            error: 'Digite valores numéricos válidos usando vírgula ou ponto decimal.',
        };
    }

    if (standardDeviation <= 0) {
        return {
            data: null,
            error: 'O desvio padrão precisa ser maior que zero.',
        };
    }

    const z = calculateZScore(raw, mean, standardDeviation);
    const percentile = calculatePercentile(z);

    return {
        data: {
            z,
            t: calculateTScore(z),
            percentile,
        },
    };
}

function CopyableMetricCard({ title, value, meta, copyText, onCopy }: CopyableMetricCardProps) {
    const isCopyDisabled = copyText === null;

    return (
        <div className="relative rounded-2xl border border-border bg-card p-4 shadow-sm">
            <div className="pr-10">
                <p className="text-xs font-medium text-muted-foreground">{title}</p>
                <div className="mt-3 text-4xl font-semibold leading-none tracking-tight text-foreground sm:text-[2.75rem]">
                    {value}
                </div>
                <div className="mt-2 text-[11px] leading-relaxed text-muted-foreground">{meta}</div>
            </div>

            <button
                type="button"
                aria-label={`Copiar ${title}`}
                disabled={isCopyDisabled}
                onClick={() => {
                    if (copyText) {
                        onCopy(copyText);
                    }
                }}
                className="absolute right-3 top-3 inline-flex size-8 items-center justify-center rounded-xl text-muted-foreground transition-colors hover:bg-brand-subtle hover:text-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-35"
            >
                <Copy className="size-4" />
            </button>
        </div>
    );
}

export default function ZScoreCalculator() {
    const [inputs, setInputs] = useState<ZScoreInputs>(DEFAULT_INPUTS);

    const calculation = useMemo(() => resolveZScore(inputs), [inputs]);
    const result = calculation.data;

    const handleInputChange = (field: ZScoreField, value: string) => {
        setInputs((currentInputs) => ({
            ...currentInputs,
            [field]: value,
        }));
    };

    const handleCopyResults = async () => {
        if (!result) {
            return;
        }

        const content = [
            `Resultado bruto: ${inputs.raw}`,
            `Média: ${inputs.mean}`,
            `Desvio padrão: ${inputs.standardDeviation}`,
            `Escore Z: ${formatMetricValue('z', result.z)}`,
            `Escore T: ${formatMetricValue('t', result.t)}`,
            `Percentil: ${formatMetricValue('percentile', result.percentile)}`,
        ].join('\n');

        try {
            await navigator.clipboard.writeText(content);
            toast.success('Cálculo copiado.');
        } catch {
            toast.error('Não foi possível copiar o cálculo.');
        }
    };

    const handleCopyMetric = async (text: string) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success('texto copiado com sucesso');
        } catch {
            toast.error('Não foi possível copiar o texto.');
        }
    };

    const zValue = result ? formatMetricValue('z', result.z) : '-';
    const tValue = result ? formatMetricValue('t', result.t) : '-';
    const percentileValue = result ? formatMetricValue('percentile', result.percentile) : '-';

    return (
        <TooltipProvider delayDuration={100}>
            <PageSection
                title={
                    <span className="inline-flex items-center gap-2">
                        <span>Calculadora de Escore Z</span>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <button
                                    type="button"
                                    aria-label="Ver fórmula do escore Z"
                                    className="inline-flex size-7 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                                >
                                    <MessageQuestion className="size-4" />
                                </button>
                            </TooltipTrigger>
                            <TooltipContent
                                side="top"
                                className="w-max max-w-[calc(100vw-2rem)] whitespace-nowrap text-xs leading-relaxed"
                            >
                                Z = (resultado bruto - média) / desvio padrão
                            </TooltipContent>
                        </Tooltip>
                    </span>
                }
                action={
                    <Button
                        type="button"
                        variant="surface"
                        disabled={!result}
                        onClick={handleCopyResults}
                    >
                        <Copy className="size-4" />
                        Copiar cálculo
                    </Button>
                }
                headerClassName="sm:items-center"
            >
                <div className="space-y-6">
                    <div className="rounded-4xl border border-border bg-surface p-4 shadow-elevation-1 sm:p-5">
                        <div className="grid gap-4 lg:grid-cols-3">
                            {FIELD_CONFIG.map((field) => (
                                <div key={field.id} className="space-y-2">
                                    <div className="flex items-center justify-between gap-3">
                                        <Label
                                            htmlFor={`z-score-${field.id}`}
                                            className="text-sm font-semibold text-foreground"
                                        >
                                            {field.label}
                                        </Label>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <button
                                                    type="button"
                                                    aria-label={`Explicação sobre ${field.label}`}
                                                    className="rounded-full text-muted-foreground transition-colors hover:text-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                                                >
                                                    <MessageQuestion className="size-4" />
                                                </button>
                                            </TooltipTrigger>
                                            <TooltipContent
                                                side="top"
                                                className="max-w-[260px] text-balance text-xs leading-relaxed"
                                            >
                                                {field.helper}
                                            </TooltipContent>
                                        </Tooltip>
                                    </div>
                                    <Input
                                        id={`z-score-${field.id}`}
                                        type="text"
                                        inputMode="decimal"
                                        placeholder={field.placeholder}
                                        value={inputs[field.id]}
                                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                                            handleInputChange(field.id, event.target.value)
                                        }
                                        className="h-12 rounded-2xl border-border bg-surface text-lg font-semibold tabular-nums shadow-none"
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    {calculation.error && (
                        <Alert variant="warning" icon={<Sparkles className="size-4" />}>
                            <span>{calculation.error}</span>
                        </Alert>
                    )}

                    <div className="grid gap-4 md:grid-cols-3">
                        <CopyableMetricCard
                            title="Escore Z"
                            value={zValue}
                            meta="Desvios padrão da média"
                            copyText={result ? `Escore Z: ${zValue}` : null}
                            onCopy={handleCopyMetric}
                        />
                        <CopyableMetricCard
                            title="Escore T equivalente"
                            value={tValue}
                            meta="Média 50, DP 10"
                            copyText={result ? `Escore T equivalente: ${tValue}` : null}
                            onCopy={handleCopyMetric}
                        />
                        <CopyableMetricCard
                            title="Percentil aproximado"
                            value={percentileValue}
                            meta="Distribuição normal"
                            copyText={result ? `Percentil aproximado: ${percentileValue}` : null}
                            onCopy={handleCopyMetric}
                        />
                    </div>
                </div>
            </PageSection>
        </TooltipProvider>
    );
}
